import React, { useState, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Download, Upload, Trash2 } from 'lucide-react';
import { exportData, importData, clearAllData } from '@/utils/storage';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { logger } from '@/utils/logger';

const DataManagement = () => {
  const { toast } = useToast();
  const [isExporting, setIsExporting] = useState(false);
  const [exportedData, setExportedData] = useState<string>('');
  const [isImporting, setIsImporting] = useState(false);
  const [isDeletingData, setIsDeletingData] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const handleExport = async () => {
    try {
      setIsExporting(true);
      const data = await exportData();
      setExportedData(data);
      
      // Create file and download
      const file = new Blob([data], { type: 'application/json' });
      const url = URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = url;
      link.download = `kid-chronicles-export-${new Date().toISOString().split('T')[0]}.json`;
      link.click();
    } catch (error) {
      logger.error("Error exporting data:", error);
      toast({
        title: "Export Failed",
        description: "There was an error exporting your data.",
        variant: "destructive"
      });
    } finally {
      setIsExporting(false);
    }
  };
  
  const triggerImport = () => {
    fileInputRef.current?.click();
  };
  
  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    try {
      setIsImporting(true);
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        const content = e.target?.result as string;
        const success = await importData(content);
        
        if (success) {
          toast({
            title: "Import Successful",
            description: "Your data has been imported successfully. Refresh the page to see your data."
          });
        } else {
          toast({
            title: "Import Failed",
            description: "There was an error importing your data.",
            variant: "destructive"
          });
        }
        setIsImporting(false);
      };
      
      reader.onerror = () => {
        toast({
          title: "Import Failed",
          description: "There was an error reading the file.",
          variant: "destructive"
        });
        setIsImporting(false);
      };
      
      reader.readAsText(file);
    } catch (error) {
      logger.error("Error importing data:", error);
      toast({
        title: "Import Failed",
        description: "There was an error importing your data.",
        variant: "destructive"
      });
      setIsImporting(false);
    }
  };
  
  const handleDeleteData = async () => {
    try {
      setIsDeletingData(true);
      await clearAllData();
      setShowConfirmDelete(false);
      
      toast({
        title: "Data Deleted",
        description: "All your data has been deleted successfully. Refresh the page to see the changes."
      });
    } catch (error) {
      logger.error("Error deleting data:", error);
      toast({
        title: "Delete Failed",
        description: "There was an error deleting your data.",
        variant: "destructive"
      });
    } finally {
      setIsDeletingData(false);
    }
  };

  return (
    <>
      <Card>
        <CardHeader className="text-center sm:text-left">
          <CardTitle>Data Management</CardTitle>
          <CardDescription>
            Import, export or delete your data
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-center sm:items-start gap-4">
              <div className="w-full text-center sm:text-left">
                <Label className="block font-medium">Export Data</Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Download a backup of all your data
                </p>
              </div>
              <Button 
                variant="outline" 
                onClick={handleExport}
                disabled={isExporting}
                className="min-w-[120px] flex items-center justify-center gap-2"
              >
                <Download className="h-4 w-4" />
                {isExporting ? "Exporting..." : "Export"}
              </Button>
            </div>
            
            <Separator className="my-4" />
            
            <div className="flex flex-col sm:flex-row justify-between items-center sm:items-start gap-4">
              <div className="w-full text-center sm:text-left">
                <Label className="block font-medium">Import Data</Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Upload a previously exported backup
                </p>
              </div>
              <Button 
                variant="outline" 
                onClick={triggerImport}
                disabled={isImporting}
                className="min-w-[120px] flex items-center justify-center gap-2"
              >
                <Upload className="h-4 w-4" />
                {isImporting ? "Importing..." : "Import"}
              </Button>
              <input 
                type="file" 
                ref={fileInputRef} 
                style={{ display: 'none' }} 
                accept=".json" 
                onChange={handleImport}
              />
            </div>
            
            <Separator className="my-4" />
            
            <div className="flex flex-col sm:flex-row justify-between items-center sm:items-start gap-4">
              <div className="w-full text-center sm:text-left">
                <Label className="block font-medium">Delete All Data</Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Permanently remove all your data
                </p>
              </div>
              <Button 
                variant="destructive" 
                onClick={() => setShowConfirmDelete(true)}
                disabled={isDeletingData}
                className="min-w-[120px] flex items-center justify-center gap-2"
              >
                <Trash2 className="h-4 w-4" />
                {isDeletingData ? "Deleting..." : "Delete All"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <AlertDialog open={showConfirmDelete} onOpenChange={setShowConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete all your data,
              including all children, quotes, and milestones.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteData} disabled={isDeletingData}>
              {isDeletingData ? "Deleting..." : "Yes, delete everything"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default DataManagement;
